import {
  Box,
  Button,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PrimaryGaurdian from './PrimaryGaurdian';
import SecondaryGaurdian from './SecondaryGaurdian';
import EmergencyGaudian from './EmergencyGaudian';
import TabPanel from 'components/TabPanel';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  deleteGuardianDetails,
  getGuardianDetails,
  updateGuardianDetails,
} from 'store/slices/userSlice';
import { toast } from 'react-toastify';
import { AuthContext, useAuth } from 'utils/AuthContext';
import { useLocation } from 'react-router-dom';
import { uploadGuradianImageAPI } from 'services/profileServices';
import GuardianProfilePicture from './GuardianProfilePicture';
import { EmergencyLabel, PrimaryLabel, SecoundaryLabel } from 'utils/functions';
import { StyledDialog } from 'theme/index';

//GaurdianInformation component
const GuardianInfo = (props: any) => {
  // states Gaurdian Info
  const [selectedTab, setSelectedTab] = useState(0);
  const [openDeleteProfileDailog, setOpenDeleteProfileDailog] = useState(false);
  const guardians: any = useAppSelector((state) => state.user.guardians);
  const [primaryPicture, setPrimaryPicture] = useState<any>(null);
  const [secondaryPicture, setSecondaryPicture] = useState<any>(null);
  const [emergencyPicture, setEmergencyPicture] = useState<any>(null);
  const [primaryGuradianInfoAvailable, setPrimaryGuradianInfoAvailable] = useState(false);
  const [secondaryGuardianInfoAvailable, setSecondaryGuardianInfoAvailable] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { state } = useLocation();
  const { setUserDetails } = useContext(AuthContext);
  const [noGuardians, setNoGuardians] = useState(guardians.length === 0);
  const isAdmin = user.isAdmin;
  const patientId = isAdmin ? state?.patientId || props?.patientId : user?.patientId;

  //handle Tab Change
  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  //handle EG tab CloseIcon
  const handleEGCloseIcon = () => {
    const emergencyGuardian = getGuardianDetailsByType('secondary');
    if (emergencyGuardian !== undefined) {
      setOpenDeleteProfileDailog(true);
    } else {
      const message = 'Please add secondary guardian before delete it.';
      toast(message, {
        type: 'info',
        icon: true,
      });
    }
  };

  //handle Delete EG Profile
  const handleDeleteEGProfile = () => {
    setOpenDeleteProfileDailog(false);
    deleteGuardian();
  };

  //handle Cancel EG Profile
  const handleCancelEGProfile = () => {
    setOpenDeleteProfileDailog(false);
  };

  const getGuardian = () => {
    if (patientId) {
      const data = {
        userId: patientId,
      };
      dispatch(getGuardianDetails(data))
        .unwrap()
        .then(async (response: any) => {
          if (response?.status === 200) {
            // eslint-disable-next-line array-callback-return
            response.data.map((x: any) => {
              if (x.guardianCategory === 'primary' && x.profileImage !== null) {
                setPrimaryPicture(x.profileImage);
              } else if (x.guardianCategory === 'secondary' && x.profileImage !== null) {
                setSecondaryPicture(x.profileImage);
              } else if (x.guardianCategory === 'emergency' && x.profileImage !== null) {
                setEmergencyPicture(x.profileImage);
              }
            });

            const secondaryGuardian: any = response.data.find(
              (guardianObj: any) => guardianObj.guardianCategory === 'secondary'
            );
            if (secondaryGuardian) {
              setSecondaryGuardianInfoAvailable(true);
            }

            const primaryGuardian: any = response.data.find(
              (guardianObj: any) => guardianObj.guardianCategory === 'primary'
            );
            if (primaryGuardian) {
              setPrimaryGuradianInfoAvailable(true);
            }
          }
        });
    }
  };

  useEffect(() => {
    getGuardian();
    //eslint-disable-next-line
  }, []);

  const moveToNextTab = (
    isSecondaryGuardianEmeregency: any,
    isPrimaryGuardianEmergency: boolean
  ) => {
    if (selectedTab === 0) setSelectedTab(1);
    else if (selectedTab === 1 && !isSecondaryGuardianEmeregency && !isPrimaryGuardianEmergency)
      setSelectedTab(2);
    else if (selectedTab === 1 && (isSecondaryGuardianEmeregency || isPrimaryGuardianEmergency))
      setSelectedTab(2);
    if (selectedTab === 2) props?.onComplete();
  };

  const skipToEGStep = () => {
    setSelectedTab(2);
  };

  const skipToPGStep = () => {
    setSelectedTab(1);
  };

  const skipStep = () => {
    props?.onComplete();
  };

  const updateGuardian = (payload: any, onSuccess: any) => {
    const patientData = {
      userId: patientId,
    };

    if (patientData?.userId) {
      if (!primaryGuradianInfoAvailable) {
        if (payload[0]?.guardianCategory === 'primary') {
          delete payload[0].id;
        }
        if (payload.length > 1 && payload[1]?.guardianCategory === 'primary') {
          delete payload[1].id;
        }
        setPrimaryGuradianInfoAvailable(true);
      } else if (!secondaryGuardianInfoAvailable && payload.length > 1) {
        if (payload[1]?.guardianCategory === 'secondary') {
          delete payload[1].id;
        }
        if (payload.length > 2 && payload[2]?.guardianCategory === 'secondary') {
          delete payload[2].id;
        }
        setSecondaryGuardianInfoAvailable(true);
      }
      const data = { param: { userId: patientData?.userId }, payload };
      return dispatch(updateGuardianDetails({ ...data }))
        .unwrap()
        .then((response) => {
          const primaryGuardian: any = response.data.find(
            (guardianObj: any) => guardianObj.guardianCategory === 'primary'
          );
          const secondaryGuardian: any = response.data.find(
            (guardianObj: any) => guardianObj.guardianCategory === 'secondary'
          );
          if (primaryGuardian) {
            const data = {
              ...user,
              relation: primaryGuardian?.relation,
            };
            setUserDetails(data);
          }
          
          moveToNextTab(secondaryGuardian?.isEmergency, primaryGuardian?.isEmergency);
          if (response.status === 200) onSuccess();
        });
    } else {
      const message = 'Please add patient details first.';
      toast(message, {
        type: 'info',
        icon: true,
      });
    }
  };

  const getGuardianDetailsByType = (type: string) => {
    const g = guardians.map((g: any) => {
      return {
        email: g.email,
        firstName: g.firstName,
        guardianCategory: g.guardianCategory,
        id: g.id,
        isActiveCustody: g.isActiveCustody,
        isEmergency: g.isEmergency,
        lastName: g.lastName,
        mobileNumber: g.mobileNumber,
        other: g.other,
        profileImage: g.profileImage,
        relation:
          g?.relation === ''
            ? ''
            : g?.relation?.charAt(0).toUpperCase() + g?.relation?.slice(1).toLowerCase(),
      };
    });
    if (guardians.length > 0) {
      return g.find((guardian: any) => guardian.guardianCategory === type);
    } else {
      return undefined;
    }
  };

  const deleteGuardian = () => {
    const { id }: any = getGuardianDetailsByType('secondary');
    if (id) {
      const params = {
        patientId: patientId,
        guardianId: id,
      };
      dispatch(deleteGuardianDetails(params))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const message = 'Guardian information deleted successfully.';
            toast(message, {
              type: 'success',
              icon: true,
            });
            setSelectedTab(1);
            getGuardian();
            window.location.reload();
          }
        });
    }
  };

  const handleProfileImage = async (acceptedFiles: any, type: string) => {
    if (acceptedFiles?.length > 0) {
      let guardianId = 0;
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (type === 'primary') {
          setPrimaryPicture(file);
        } else if (type === 'secondary') {
          setSecondaryPicture(file);
        } else {
          setEmergencyPicture(file);
        }
      };
      reader.readAsDataURL(file);
      const formData: any = new FormData();
      formData.append('file', file);

      if (type === 'primary') {
        // eslint-disable-next-line
        guardians.map((g: any) => {
          if (g.guardianCategory === 'primary') {
            guardianId = g.id;
            return guardianId;
          }
        });
      } else if (type === 'secondary') {
        // eslint-disable-next-line array-callback-return
        guardians.map((g: any) => {
          if (g.guardianCategory === 'secondary') {
            guardianId = g.id;
            return guardianId;
          }
        });
      } else {
        // eslint-disable-next-line array-callback-return
        guardians.map((g: any) => {
          if (g.guardianCategory === 'emergency') {
            guardianId = g.id;
            return guardianId;
          }
        });
      }

      await uploadGuradianImageAPI(
        formData,
        patientId,
        guardianId
      );
    }
  };

  // return GaurdianInformation component
  return (
    <>
      <Container component='main' maxWidth='sm'>
        {/* Tabs for primary, secondary , emergency gaurdaian */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: { xs: 320, sm: '100%' },
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons
            allowScrollButtonsMobile
          >
            <Tab
              label={
                <PrimaryLabel  selectedTab={selectedTab} />
              }
              icon={
                <GuardianProfilePicture
                  // eslint-disable-next-line max-len
                  onFileUpload={(acceptedFiles: any) =>
                    handleProfileImage(acceptedFiles, 'primary')
                  }
                  profileImage={true}
                  profilePicture={primaryPicture}
                  defaultPicture={
                    primaryPicture === null ? '../images/primary_guardian.png' : primaryPicture
                  }
                  selectedTab={selectedTab === 0}
                  guardianDetails={getGuardianDetailsByType('primary')}
                />
              }
            />

            <Tab
              disabled={guardians.length === 0}
              label={
                <SecoundaryLabel
                  selectedTab={selectedTab}
                />
              }
              icon={
                <GuardianProfilePicture
                  // eslint-disable-next-line max-len
                  onFileUpload={(acceptedFiles: any) =>
                    handleProfileImage(acceptedFiles, 'secondary')
                  }
                  profileImage={true}
                  profilePicture={secondaryPicture}
                  defaultPicture={
                    secondaryPicture === null
                      ? '../images/secondary_guardian.png'
                      : secondaryPicture
                  }
                  selectedTab={selectedTab === 1}
                  guardianDetails={getGuardianDetailsByType('secondary')}
                />
              }
            />
            <Tab
              disabled={guardians.length === 0}
              label={
                <EmergencyLabel
                  selectedTab={selectedTab}
                />
              }
              icon={
                <GuardianProfilePicture
                  // eslint-disable-next-line max-len
                  onFileUpload={(acceptedFiles: any) =>
                    handleProfileImage(acceptedFiles, 'emergency')
                  }
                  profileImage={true}
                  profilePicture={emergencyPicture}
                  defaultPicture={
                    emergencyPicture === null
                      ? '../images/emergency_guardian.png'
                      : emergencyPicture
                  }
                  selectedTab={selectedTab === 2}
                />
              }
            />
          </Tabs>
          <TabPanel value={selectedTab} index={0}>
            {/* Content for Tab 1 */}
            <PrimaryGaurdian
              skipStep={skipToPGStep}
              onUpdateGuardian={updateGuardian}
              primaryGaurdian={getGuardianDetailsByType('primary')}
              pictureAvailable={primaryPicture !== null ? true : false}
            />
          </TabPanel>
          <TabPanel value={selectedTab} index={1} disable={noGuardians}>
            {/* Content for Tab 2 */}
            <SecondaryGaurdian
              onUpdateGuardian={updateGuardian}
              handleDeleteGuardian={handleEGCloseIcon}
              secondaryGaurdian={getGuardianDetailsByType('secondary')}
              pictureAvailable={secondaryPicture !== null ? true : false}
              skipStep={skipToEGStep}
              disableSaveButton={guardians.length === 0}
            />
          </TabPanel>
          <TabPanel value={selectedTab} index={2} disable={guardians.length === 0}>
            {/* Content for Tab 3 */}
            <EmergencyGaudian
              skipStep={skipStep}
              disableSaveButton={guardians.length === 0}
            />
          </TabPanel>
        </Box>

        <StyledDialog
          open={openDeleteProfileDailog}
          onClose={handleCancelEGProfile}
          maxWidth='xs'
          fullWidth
        >
          {/* Close Icon */}
          <IconButton
            aria-label='Close'
            onClick={handleCancelEGProfile}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>

          {/* Title */}
          <DialogTitle
            sx={{
              textAlign: 'center',
              pb: 1,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Delete Secondary Guardian Information
          </DialogTitle>

          {/* Message */}
          <DialogContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography sx={{ fontSize: '13px' }}>
              Are you sure you want to delete the Secondary Guardian?
            </Typography>
          </DialogContent>

          {/* Buttons */}
          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 1,
              pb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              variant='contained'
              size='small'
              sx={{
                minWidth: 90,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                borderRadius: '16px',
                textTransform: 'none',
              }}
              onClick={handleCancelEGProfile}
            >
              Cancel
            </Button>

            <Button
              variant='contained'
              size='small'
              sx={{
                minWidth: 90,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                borderRadius: '16px',
                textTransform: 'none',
              }}
              onClick={handleDeleteEGProfile}
            >
              Delete
            </Button>
          </DialogActions>
        </StyledDialog>
      </Container>
    </>
  );
};

export default GuardianInfo;
